import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  ExternalLink,
  Filter,
  Plus,
  RefreshCw,
  Search,
  Settings2,
  Ticket,
  Trash2,
} from "lucide-react";
import { useAuth } from "../../utils/auth";
import {
  canViewTickets,
  canCreateTickets,
  canManageTicketProblemTypes,
  canChangeTicketStatus,
  canDeleteTickets,
} from "../../utils/roles";
import {
  fetchTickets,
  fetchTicketStatusCounts,
  fetchTicketProblemTypes,
  fetchAssignableUsers,
  updateTicketStatus,
  deleteTicket,
  TICKET_PRIORITIES,
  TICKET_STATUSES,
} from "../../api/TicketAPIs";
import { AttachmentPreview, TicketStatusBadge, TicketPriorityBadge } from "./TicketBadges";
import {
  PRIORITY_LABELS_AR,
  STATUS_LABELS_AR,
  formatDateTime,
} from "./ticketUi";

const PAGE_SIZE = 20;

const STATUS_TABS = [
  { key: "All", label: "الكل", countKey: "all" },
  { key: "Open", label: "مفتوحة", countKey: "open" },
  { key: "In Progress", label: "قيد المعالجة", countKey: "inProgress" },
  { key: "Waiting for Customer", label: "بانتظار العميل", countKey: "waitingForCustomer" },
  { key: "Resolved", label: "تم الحل", countKey: "resolved" },
  { key: "Closed", label: "مغلقة", countKey: "closed" },
];

const emptyFilters = {
  search: "",
  priority: "",
  problemTypeId: "",
  createdByUserId: "",
  assignedToUserId: "",
  orderId: "",
  customerPhone: "",
  fromDateUtc: "",
  toDateUtc: "",
  sortBy: "createdAt",
  sortOrder: "desc",
};

const RESOLVED_STATUSES = new Set(["Resolved", "Closed", "Invalid / No Issue Found"]);

const ticketIssues = (t) => t.issues || t.Issues || [];
const ticketAttachments = (t) => t.attachments || t.Attachments || [];

const collectTicketMedia = (t) => {
  const media = [];
  const seen = new Set();
  const push = (item) => {
    if (!item) return;
    const path = item.filePath || item.FilePath || item.snapshotImageUrl || item.SnapshotImageUrl || item;
    if (!path || typeof path !== "string" || seen.has(path)) return;
    seen.add(path);
    if (typeof item === "string") {
      media.push({ filePath: item, fileType: "image/jpeg" });
    } else {
      media.push(item);
    }
  };

  ticketIssues(t).forEach((issue) => {
    const snap = issue.snapshotImageUrl || issue.SnapshotImageUrl;
    if (snap) push({ filePath: snap, fileType: "image/jpeg", fileName: "المنتج" });
    (issue.attachments || issue.Attachments || []).forEach(push);
  });
  (t.affectedItems || t.AffectedItems || []).forEach((item) => {
    push(item.snapshotImageUrl || item.SnapshotImageUrl);
  });
  ticketAttachments(t).forEach(push);
  return media;
};

const TicketCard = ({
  ticket,
  canStatus,
  canDelete,
  updating,
  extraOpen,
  onToggleExtra,
  onStatusChange,
  onResolve,
  onDelete,
  onOpen,
}) => {
  const issues = ticketIssues(ticket);
  const media = collectTicketMedia(ticket);
  const status = ticket.status || ticket.Status;
  const alreadyDone = RESOLVED_STATUSES.has(status);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          {canStatus && !alreadyDone && (
            <button
              type="button"
              disabled={updating}
              onClick={() => onResolve(ticket)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700 disabled:opacity-50"
            >
              <CheckCircle2 size={16} />
              اتحلت
            </button>
          )}
          {canDelete && alreadyDone && (
            <button
              type="button"
              disabled={updating}
              onClick={() => onDelete(ticket)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-red-600 text-white text-sm font-bold hover:bg-red-700 disabled:opacity-50"
            >
              <Trash2 size={16} />
              مسح
            </button>
          )}
          <TicketStatusBadge status={status} />
        </div>
        <div className="text-left shrink-0">
          <div className="font-bold text-indigo-700">#{ticket.id}</div>
          <div className="text-xs text-gray-500">طلب #{ticket.orderId}</div>
        </div>
      </div>

      <div className="space-y-2">
        {issues.length > 0 ? (
          issues.map((issue) => (
            <div key={issue.id || issue.Id} className="bg-slate-50 rounded-xl p-3">
              <div className="font-bold text-gray-900">
                {issue.problemTypeName || issue.ProblemTypeName || "مشكلة"}
              </div>
              {(issue.snapshotDesignCode || issue.SnapshotDesignCode || issue.snapshotDesignName || issue.SnapshotDesignName) && (
                <div className="text-xs text-gray-500 mt-0.5">
                  {issue.snapshotDesignCode || issue.SnapshotDesignCode || issue.snapshotDesignName || issue.SnapshotDesignName}
                  {(issue.snapshotProductModel || issue.SnapshotProductModel) ? ` — ${issue.snapshotProductModel || issue.SnapshotProductModel}` : ""}
                </div>
              )}
              {(issue.description || issue.Description) && (
                <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">
                  {issue.description || issue.Description}
                </p>
              )}
            </div>
          ))
        ) : (
          <div className="bg-slate-50 rounded-xl p-3 font-bold text-gray-900">
            {ticket.orderLevelProblemType || "مشكلة على مستوى الطلب"}
          </div>
        )}
      </div>

      {media.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {media.map((att, idx) => (
            <AttachmentPreview
              key={att.id || att.Id || att.filePath || att.FilePath || idx}
              attachment={att}
              compact
            />
          ))}
        </div>
      )}

      {canStatus && (
        <div className="flex flex-col sm:flex-row gap-2">
          <select
            value={status}
            disabled={updating}
            onChange={(e) => onStatusChange(ticket, e.target.value)}
            className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-xl text-sm bg-white"
          >
            {TICKET_STATUSES.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS_AR[s]}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="flex items-center justify-between gap-2 pt-1">
        <button
          type="button"
          onClick={onToggleExtra}
          className="text-xs text-gray-500 hover:text-gray-800 flex items-center gap-1"
        >
          بيانات إضافية
          <ChevronDown size={14} className={extraOpen ? "rotate-180 transition" : "transition"} />
        </button>
        <button
          type="button"
          onClick={onOpen}
          className="text-sm text-indigo-600 font-semibold inline-flex items-center gap-1"
        >
          عرض التفاصيل
          <ExternalLink size={14} />
        </button>
      </div>

      {extraOpen && (
        <div className="grid grid-cols-2 gap-2 text-xs bg-gray-50 rounded-xl p-3 text-gray-600">
          <div>
            <div className="text-gray-400">العميل</div>
            <div className="font-medium text-gray-800">{ticket.customerName || "—"}</div>
            <div>{ticket.customerPhone || ""}</div>
          </div>
          <div>
            <div className="text-gray-400">الأولوية</div>
            <TicketPriorityBadge priority={ticket.priority} />
          </div>
          <div>
            <div className="text-gray-400">أنشئت بواسطة</div>
            <div className="font-medium text-gray-800">{ticket.createdByEmail || "—"}</div>
          </div>
          <div>
            <div className="text-gray-400">مسندة إلى</div>
            <div className="font-medium text-gray-800">{ticket.assignedToEmail || "غير مسندة"}</div>
          </div>
          <div className="col-span-2">
            <div className="text-gray-400">التاريخ</div>
            <div>{formatDateTime(ticket.createdAtUtc)}</div>
          </div>
        </div>
      )}
    </div>
  );
};

const TicketsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { authState } = useAuth();
  const canView = canViewTickets(authState.role);
  const canCreate = canCreateTickets(authState.role);
  const canManageTypes = canManageTicketProblemTypes(authState.role);
  const canStatus = canChangeTicketStatus(authState.role);
  const canDelete = canDeleteTickets(authState.role);

  const [statusTab, setStatusTab] = useState("All");
  const [filters, setFilters] = useState(emptyFilters);
  const [applied, setApplied] = useState(emptyFilters);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(0);
  const [updatingId, setUpdatingId] = useState(null);
  const [openExtra, setOpenExtra] = useState({});

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!authState.isValidating && !canView) navigate("/login");
  }, [authState, canView, navigate]);

  const queryParams = useMemo(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      status: statusTab === "All" ? "" : statusTab,
      ...applied,
      problemTypeId: applied.problemTypeId || undefined,
      createdByUserId: applied.createdByUserId || undefined,
      assignedToUserId: applied.assignedToUserId || undefined,
      orderId: applied.orderId || undefined,
      fromDateUtc: applied.fromDateUtc
        ? new Date(applied.fromDateUtc).toISOString()
        : undefined,
      toDateUtc: applied.toDateUtc ? new Date(applied.toDateUtc).toISOString() : undefined,
    }),
    [page, statusTab, applied]
  );

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ["tickets", queryParams],
    queryFn: () => fetchTickets(queryParams),
    enabled: canView,
    keepPreviousData: true,
  });

  const { data: counts, refetch: refetchCounts } = useQuery({
    queryKey: ["ticketStatusCounts"],
    queryFn: fetchTicketStatusCounts,
    enabled: canView,
  });

  const { data: problemTypes = [] } = useQuery({
    queryKey: ["ticketProblemTypes", "all"],
    queryFn: () => fetchTicketProblemTypes(false),
    enabled: canView,
  });

  const { data: users = [] } = useQuery({
    queryKey: ["ticketAssignableUsers"],
    queryFn: fetchAssignableUsers,
    enabled: canCreate,
  });

  const applyFilters = () => {
    setPage(0);
    setApplied({ ...filters });
  };

  const resetFilters = () => {
    setFilters(emptyFilters);
    setApplied(emptyFilters);
    setPage(0);
  };

  const refreshTickets = () => {
    queryClient.invalidateQueries({ queryKey: ["tickets"] });
    queryClient.invalidateQueries({ queryKey: ["ticketStatusCounts"] });
  };

  const changeStatus = async (ticket, nextStatus) => {
    if (!nextStatus || nextStatus === (ticket.status || ticket.Status)) return;
    setUpdatingId(ticket.id);
    try {
      await updateTicketStatus(ticket.id, nextStatus);
      toast.success(nextStatus === "Resolved" ? "تم تعليم التذكرة كمحلولة" : "تم تحديث الحالة");
      refreshTickets();
    } catch (err) {
      toast.error(err.message || "فشل تحديث الحالة");
    } finally {
      setUpdatingId(null);
    }
  };

  const removeTicket = async (ticket) => {
    if (!window.confirm(`مسح التذكرة #${ticket.id}؟\nلا يمكن التراجع عن هذه العملية.`)) return;
    setUpdatingId(ticket.id);
    try {
      await deleteTicket(ticket.id);
      toast.success("تم مسح التذكرة");
      refreshTickets();
    } catch (err) {
      toast.error(err.message || "فشل مسح التذكرة");
    } finally {
      setUpdatingId(null);
    }
  };

  const items = data?.items || [];
  const totalCount = data?.totalCount || 0;
  const hasMore = data?.hasMore;

  if (!canView) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 md:p-8" dir="rtl">
      <ToastContainer position="top-center" />
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/dashboard")}
              className="p-2 rounded-xl bg-white shadow hover:bg-gray-50"
            >
              <ChevronRight size={22} />
            </button>
            <div>
              <h1 className="text-2xl md:text-4xl font-bold text-gray-800 flex items-center gap-2">
                <Ticket className="text-indigo-600" />
                إدارة التذاكر
              </h1>
              <p className="text-gray-500 mt-1">متابعة وحل مشاكل الطلبات</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => {
                refetch();
                refetchCounts();
              }}
              className="px-4 py-2 rounded-xl bg-white border border-gray-200 text-gray-700 flex items-center gap-2"
            >
              <RefreshCw size={16} className={isFetching ? "animate-spin" : ""} />
              تحديث
            </button>
            {canManageTypes && (
              <button
                onClick={() => navigate("/tickets/problem-types")}
                className="px-4 py-2 rounded-xl bg-white border border-gray-200 text-gray-700 flex items-center gap-2"
              >
                <Settings2 size={16} />
                أنواع المشاكل
              </button>
            )}
            {canCreate && (
              <button
                onClick={() => navigate("/tickets/create")}
                className="px-4 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white flex items-center gap-2 shadow"
              >
                <Plus size={16} />
                تذكرة جديدة
              </button>
            )}
          </div>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2 mb-4">
          {STATUS_TABS.map((tab) => {
            const count = counts?.[tab.countKey] ?? 0;
            const active = statusTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => {
                  setStatusTab(tab.key);
                  setPage(0);
                }}
                className={`whitespace-nowrap px-4 py-2 rounded-xl text-sm font-semibold border transition ${
                  active
                    ? "bg-indigo-600 text-white border-indigo-600"
                    : "bg-white text-gray-700 border-gray-200 hover:border-indigo-300"
                }`}
              >
                {tab.label}
                <span className={`mr-2 px-2 py-0.5 rounded-full text-xs ${active ? "bg-white/20" : "bg-gray-100"}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="bg-white/90 rounded-2xl shadow border border-gray-100 p-4 mb-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                value={filters.search}
                onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                placeholder="بحث برقم التذكرة / الطلب / الاسم / الهاتف..."
                className="w-full pr-10 pl-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-purple-200 outline-none"
              />
            </div>
            <button
              onClick={() => setShowFilters((v) => !v)}
              className="px-4 py-3 rounded-xl border-2 border-gray-200 bg-white flex items-center gap-2"
            >
              <Filter size={16} />
              فلاتر
            </button>
            <button
              onClick={applyFilters}
              className="px-5 py-3 rounded-xl bg-indigo-600 text-white font-semibold"
            >
              تطبيق
            </button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4 pt-4 border-t border-gray-100">
              <select
                value={filters.priority}
                onChange={(e) => setFilters((f) => ({ ...f, priority: e.target.value }))}
                className="px-3 py-2 border-2 border-gray-200 rounded-xl"
              >
                <option value="">كل الأولويات</option>
                {TICKET_PRIORITIES.map((p) => (
                  <option key={p} value={p}>
                    {PRIORITY_LABELS_AR[p]}
                  </option>
                ))}
              </select>
              <select
                value={filters.problemTypeId}
                onChange={(e) => setFilters((f) => ({ ...f, problemTypeId: e.target.value }))}
                className="px-3 py-2 border-2 border-gray-200 rounded-xl"
              >
                <option value="">كل أنواع المشاكل</option>
                {problemTypes.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              {canCreate && (
                <>
                  <select
                    value={filters.createdByUserId}
                    onChange={(e) => setFilters((f) => ({ ...f, createdByUserId: e.target.value }))}
                    className="px-3 py-2 border-2 border-gray-200 rounded-xl"
                  >
                    <option value="">أنشئت بواسطة</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.email}
                      </option>
                    ))}
                  </select>
                  <select
                    value={filters.assignedToUserId}
                    onChange={(e) => setFilters((f) => ({ ...f, assignedToUserId: e.target.value }))}
                    className="px-3 py-2 border-2 border-gray-200 rounded-xl"
                  >
                    <option value="">مسندة إلى</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.email}
                      </option>
                    ))}
                  </select>
                </>
              )}
              <input
                type="number"
                placeholder="رقم الطلب"
                value={filters.orderId}
                onChange={(e) => setFilters((f) => ({ ...f, orderId: e.target.value }))}
                className="px-3 py-2 border-2 border-gray-200 rounded-xl"
              />
              <input
                placeholder="رقم هاتف العميل"
                value={filters.customerPhone}
                onChange={(e) => setFilters((f) => ({ ...f, customerPhone: e.target.value }))}
                className="px-3 py-2 border-2 border-gray-200 rounded-xl"
              />
              <input
                type="datetime-local"
                value={filters.fromDateUtc}
                onChange={(e) => setFilters((f) => ({ ...f, fromDateUtc: e.target.value }))}
                className="px-3 py-2 border-2 border-gray-200 rounded-xl"
              />
              <input
                type="datetime-local"
                value={filters.toDateUtc}
                onChange={(e) => setFilters((f) => ({ ...f, toDateUtc: e.target.value }))}
                className="px-3 py-2 border-2 border-gray-200 rounded-xl"
              />
              <select
                value={`${filters.sortBy}:${filters.sortOrder}`}
                onChange={(e) => {
                  const [sortBy, sortOrder] = e.target.value.split(":");
                  setFilters((f) => ({ ...f, sortBy, sortOrder }));
                }}
                className="px-3 py-2 border-2 border-gray-200 rounded-xl"
              >
                <option value="createdAt:desc">الأحدث أولاً</option>
                <option value="createdAt:asc">الأقدم أولاً</option>
                <option value="priority:desc">الأولوية ↓</option>
                <option value="status:asc">الحالة</option>
                <option value="orderId:desc">رقم الطلب ↓</option>
              </select>
              <button onClick={resetFilters} className="px-3 py-2 rounded-xl border border-gray-300 text-gray-600">
                مسح الفلاتر
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="bg-red-50 text-red-700 p-4 rounded-xl mb-4">{error.message}</div>
        )}

        <div className="space-y-3">
          {isLoading ? (
            <div className="bg-white/90 rounded-2xl shadow border border-gray-100 p-10 text-center text-gray-500">
              جاري التحميل...
            </div>
          ) : items.length === 0 ? (
            <div className="bg-white/90 rounded-2xl shadow border border-gray-100 p-10 text-center text-gray-500">
              لا توجد تذاكر
            </div>
          ) : (
            items.map((t) => (
              <TicketCard
                key={t.id}
                ticket={t}
                canStatus={canStatus}
                canDelete={canDelete}
                updating={updatingId === t.id}
                extraOpen={!!openExtra[t.id]}
                onToggleExtra={() =>
                  setOpenExtra((prev) => ({ ...prev, [t.id]: !prev[t.id] }))
                }
                onStatusChange={changeStatus}
                onResolve={(ticket) => changeStatus(ticket, "Resolved")}
                onDelete={removeTicket}
                onOpen={() => navigate(`/tickets/${t.id}`)}
              />
            ))
          )}
        </div>

        <div className="flex items-center justify-between mt-4">
          <span className="text-sm text-gray-500">
            الإجمالي: {totalCount} — صفحة {page + 1}
          </span>
          <div className="flex gap-2">
            <button
              disabled={page <= 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="px-4 py-2 rounded-xl bg-white border disabled:opacity-40"
            >
              السابق
            </button>
            <button
              disabled={!hasMore}
              onClick={() => setPage((p) => p + 1)}
              className="px-4 py-2 rounded-xl bg-white border disabled:opacity-40"
            >
              التالي
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketsPage;
